import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { MoreHorizontal, Pencil, Trash2, Users, X } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { AvatarUpload } from "@/components/AvatarUpload";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Sheet, SheetBody, SheetClose, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { StatusBadge } from "@/components/StatusBadge";
import { Field as InfoRow } from "@/features/client-report/ClientReportSections";
import { SectionCard, SectionEmptyState } from "@/features/admin/SectionCard";
import { useAdvisorActions } from "@/features/admin/useAdvisorActions";
import { changePassword } from "@/lib/api/auth";
import { setEmailNotificationsEnabled } from "@/lib/api/advisors";
import { useDialog } from "@/lib/dialogs/DialogProvider";
import { useAsyncAction } from "@/lib/hooks/useAsyncAction";
import { disablePush, enablePush, getPushSubscriptionState } from "@/lib/pwa/push";
import { useSession } from "@/lib/session/SessionContext";
import { getInitials, joinName, splitName } from "@/lib/utils";
import type { Advisor } from "@/lib/types/session";

/** Kopf-Styling wie ClientDetailsSheet: bg-primary, Button-Varianten für die dunkle Fläche angepasst. */
const ON_DARK_GHOST = "text-primary-foreground/80 hover:bg-primary-foreground/15 hover:text-primary-foreground aria-expanded:bg-primary-foreground/15 aria-expanded:text-primary-foreground";

function ChangePasswordDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { alert } = useDialog();
  const { run, pending } = useAsyncAction();
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    run(async () => {
      if (password !== confirmation) throw new Error("Die Passwörter stimmen nicht überein.");
      await changePassword(password);
      onOpenChange(false);
      setPassword("");
      setConfirmation("");
      await alert("Passwort erfolgreich geändert.", { title: "Passwort geändert" });
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Passwort ändern</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="advisor-sheet-new-pass">Neues Passwort</FieldLabel>
              <Input id="advisor-sheet-new-pass" type="password" autoComplete="new-password" placeholder="Mindestens 12 Zeichen" required minLength={12} value={password} onChange={(event) => setPassword(event.target.value)} />
            </Field>
            <Field>
              <FieldLabel htmlFor="advisor-sheet-new-pass-confirm">Neues Passwort bestätigen</FieldLabel>
              <Input id="advisor-sheet-new-pass-confirm" type="password" autoComplete="new-password" placeholder="Passwort wiederholen" required minLength={12} value={confirmation} onChange={(event) => setConfirmation(event.target.value)} />
            </Field>
          </FieldGroup>
          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={pending} className="hover:bg-primary-dark">
              Passwort speichern
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SettingRow({ id, label, hint, checked, disabled, onCheckedChange }: { id: string; label: string; hint: string; checked: boolean; disabled: boolean; onCheckedChange: () => void }) {
  return (
    <div className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0">
      <div className="min-w-0">
        <p id={id} className="text-sm font-medium">
          {label}
        </p>
        <p className="text-sm text-muted-foreground">{hint}</p>
      </div>
      <Switch aria-labelledby={id} checked={checked} disabled={disabled} onCheckedChange={onCheckedChange} />
    </div>
  );
}

/**
 * Ein Berater — das eigene Profil (Kontomenü) und die Detailansicht im Team
 * (Einstellungen) sind dieselbe Ansicht; vorher waren es zwei Komponenten, die
 * auseinanderliefen. Bei dir selbst kommen Anmeldung und Benachrichtigungen dazu,
 * bei anderen die zugewiesenen Kunden und — wo erlaubt — das Löschen.
 */
export function AdvisorSheet({ advisor, open, onOpenChange, onExited }: { advisor: Advisor | null; open: boolean; onOpenChange: (open: boolean) => void; onExited?: () => void }) {
  const { session, clients, setAdvisors } = useSession();
  const { submitAdvisor, deleteAdvisorAction } = useAdvisorActions();
  const contactAction = useAsyncAction();
  const pushAction = useAsyncAction();
  const emailNotificationsAction = useAsyncAction();

  const [isEditing, setIsEditing] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [avatar, setAvatar] = useState("");
  const [pushSubscribed, setPushSubscribed] = useState(false);

  const isSelf = session?.actorType === "admin" && Boolean(advisor) && session.user.id === advisor?.id;

  const resetForm = () => {
    if (!advisor) return;
    const split = splitName(advisor.name);
    setFirstName(split.firstName);
    setLastName(split.lastName);
    setEmail(advisor.email);
    setPhone(advisor.phone);
    setAvatar(advisor.avatar);
  };

  // Felder neu befüllen und Edit-Modus verlassen, sobald ein anderer Berater geöffnet wird.
  useEffect(() => {
    resetForm();
    setIsEditing(false);
  }, [advisor?.id]);

  useEffect(() => {
    if (!isSelf) return;
    getPushSubscriptionState().then(setPushSubscribed).catch(() => setPushSubscribed(false));
  }, [isSelf]);

  if (!advisor) return null;

  const canDelete = !isSelf && advisor.role === "advisor";
  const assignedClients = clients.filter((client) => client.primaryAdvisorId === advisor.id || client.backupAdvisorId === advisor.id);

  const handleSave = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    contactAction.run(async () => {
      const result = await submitAdvisor({ id: advisor.id, name: joinName(firstName, lastName), email, phone, avatar });
      if (result) setIsEditing(false);
    });
  };

  const handleTogglePush = () => {
    pushAction.run(async () => {
      if (pushSubscribed) {
        await disablePush();
        setPushSubscribed(false);
      } else {
        await enablePush();
        setPushSubscribed(true);
      }
    });
  };

  const handleToggleEmailNotifications = () => {
    emailNotificationsAction.run(async () => {
      const next = !advisor.emailNotificationsEnabled;
      await setEmailNotificationsEnabled(next);
      setAdvisors((list) => list.map((entry) => (entry.id === advisor.id ? { ...entry, emailNotificationsEnabled: next } : entry)));
    });
  };

  return (
    <>
      <Sheet
        open={open}
        onOpenChange={onOpenChange}
        onOpenChangeComplete={(isOpen) => {
          if (isOpen) return;
          setIsEditing(false);
          onExited?.();
        }}
      >
        <SheetContent showCloseButton={false}>
          <SheetHeader className="gap-4 border-b-0 bg-primary -ml-[1px] pt-3 pb-5 text-primary-foreground">
            <div className="flex items-center justify-end gap-1">
              {canDelete ? (
                <DropdownMenu>
                  <DropdownMenuTrigger render={<Button size="icon-sm" variant="ghost" className={ON_DARK_GHOST} aria-label="Weitere Aktionen" />}>
                    <MoreHorizontal className="size-4" aria-hidden="true" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem className="text-destructive hover:bg-destructive-fade" onClick={() => deleteAdvisorAction(advisor.id)}>
                      <Trash2 aria-hidden="true" /> Löschen
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : null}

              <SheetClose render={<Button size="icon-sm" variant="ghost" className={ON_DARK_GHOST} aria-label="Schließen" />}>
                <X className="size-4" aria-hidden="true" />
              </SheetClose>
            </div>

            <div className="mt-1 flex flex-wrap items-end gap-x-4 gap-y-4">
              <Avatar size="lg" className="size-14 self-center bg-card shadow-sm outline -outline-offset-1 outline-white/50">
                {advisor.avatar ? <AvatarImage src={advisor.avatar} alt="" /> : null}
                <AvatarFallback className="bg-card text-base font-semibold text-primary">{getInitials(advisor.name)}</AvatarFallback>
              </Avatar>

              <div className="min-w-0 flex-1 basis-48">
                <p className="truncate text-sm text-primary-foreground/70">{isSelf ? "Mein Profil" : "Berater"}</p>
                <SheetTitle className="flex items-center gap-2 truncate text-2xl text-primary-foreground">
                  {advisor.name}
                  {advisor.role === "superadmin" ? <StatusBadge tone="blue">Admin</StatusBadge> : null}
                </SheetTitle>
              </div>
            </div>
          </SheetHeader>

          <SheetBody className="flex flex-col gap-6 bg-background p-6">
            <SectionCard
              title="Persönliche Daten"
              action={
                isEditing ? null : (
                  <Button size="sm" variant="outline" onClick={() => setIsEditing(true)}>
                    <Pencil className="size-3.5" aria-hidden="true" /> Bearbeiten
                  </Button>
                )
              }
            >
              {isEditing ? (
                <form onSubmit={handleSave} className="flex flex-col gap-4">
                  <FieldGroup>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <Field>
                        <FieldLabel htmlFor="advisor-sheet-first-name">Vorname</FieldLabel>
                        <Input id="advisor-sheet-first-name" required value={firstName} onChange={(event) => setFirstName(event.target.value)} />
                      </Field>
                      <Field>
                        <FieldLabel htmlFor="advisor-sheet-last-name">Nachname</FieldLabel>
                        <Input id="advisor-sheet-last-name" required value={lastName} onChange={(event) => setLastName(event.target.value)} />
                      </Field>
                    </div>
                    <Field>
                      <FieldLabel htmlFor="advisor-sheet-email">E-Mail-Adresse</FieldLabel>
                      <Input id="advisor-sheet-email" type="email" required value={email} onChange={(event) => setEmail(event.target.value)} />
                    </Field>
                    <Field>
                      <FieldLabel htmlFor="advisor-sheet-phone">Telefonnummer</FieldLabel>
                      <Input id="advisor-sheet-phone" required value={phone} onChange={(event) => setPhone(event.target.value)} />
                    </Field>
                    <Field>
                      <FieldLabel>Profilbild (optional)</FieldLabel>
                      <AvatarUpload value={avatar} onChange={setAvatar} name={joinName(firstName, lastName)} />
                    </Field>
                  </FieldGroup>
                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        resetForm();
                        setIsEditing(false);
                      }}
                    >
                      Abbrechen
                    </Button>
                    <Button type="submit" disabled={contactAction.pending} className="hover:bg-primary-dark">
                      Speichern
                    </Button>
                  </div>
                </form>
              ) : (
                <dl className="-my-3 divide-y divide-border">
                  <InfoRow label="E-Mail" value={advisor.email} />
                  <InfoRow label="Telefon" value={advisor.phone} />
                  <InfoRow label="Rolle" value={advisor.role === "superadmin" ? "Admin" : "Berater"} />
                </dl>
              )}
            </SectionCard>

            {isSelf ? (
              <>
                <SectionCard title="Anmeldung">
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <p className="text-sm font-medium">Passwort</p>
                      <p className="text-sm text-muted-foreground">Mindestens 12 Zeichen.</p>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => setPasswordOpen(true)}>
                      Passwort ändern
                    </Button>
                  </div>
                </SectionCard>

                <SectionCard title="Benachrichtigungen" description="Bei neuen Mitteilungen, z. B. abgeschlossenen Onboardings.">
                  <div className="flex flex-col divide-y divide-border">
                    <SettingRow id="advisor-sheet-mail-label" label="Per E-Mail" hint={`An ${advisor.email}`} checked={advisor.emailNotificationsEnabled} disabled={emailNotificationsAction.pending} onCheckedChange={handleToggleEmailNotifications} />
                    <SettingRow id="advisor-sheet-push-label" label="Push auf diesem Gerät" hint="Gilt nur für diesen Browser – auf anderen Geräten dort einschalten." checked={pushSubscribed} disabled={pushAction.pending} onCheckedChange={handleTogglePush} />
                  </div>
                </SectionCard>
              </>
            ) : (
              <SectionCard title={`Zugewiesene Kunden (${assignedClients.length})`}>
                {assignedClients.length === 0 ? (
                  <SectionEmptyState icon={Users}>Diesem Berater sind aktuell keine Kunden zugeordnet.</SectionEmptyState>
                ) : (
                  <ul className="-my-2.5 flex flex-col divide-y divide-border">
                    {assignedClients.map((client) => (
                      <li key={client.id} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                        <div className="min-w-0">
                          <Link to={`/admin?client=${client.id}`} className="font-medium text-primary hover:underline">
                            {client.name}
                          </Link>
                          <span className="ml-2 text-xs text-muted-foreground">{client.primaryAdvisorId === advisor.id ? "Erst-Bearbeiter" : "Ersatz-Bearbeiter"}</span>
                        </div>
                        <StatusBadge tone={client.status === "Abgeschlossen" || client.status === "Final gesperrt" ? "green" : "yellow"}>{client.status}</StatusBadge>
                      </li>
                    ))}
                  </ul>
                )}
              </SectionCard>
            )}
          </SheetBody>
        </SheetContent>
      </Sheet>
      {isSelf ? <ChangePasswordDialog open={passwordOpen} onOpenChange={setPasswordOpen} /> : null}
    </>
  );
}
